import { ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { FilterType } from '@/coreDomainsModule/entities/filter';
import { TableContentHeader } from '../BaseTableContent/interface/tableContentHeaders';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * フォームに現れるフィールド項目名
 */
export const formOptionsAggregateBusiness = [
  'warehouseNumber',
  'warehouseName',
  'supplierNumber',
  'supplierName',
  'businessNumber',
  'businessName',
  'package',
  'unit',
  'manufacturer',
  'aggregateBusinessResponsibleUser',
  'userUniqueNumber',
  'leadTime',
  'leadTimeUnit',
  'inTransitDays',
  'MOQ',
  'orderPackage',
  'purchaseUnitPrice',
  'stdInventoryIndicesMin',
  'stdInventoryIndicesMax',
  'stdInventoryIndicesUnit',
] as const;

export const formOptionsBusiness = [
  'customerNumber',
  'customerName',
  'salesDestinationNumber',
  'salesDestinationName',
  'endUser',
  'salesUnitPrice',
  'baseBusinessResponsibleUser',
] as const;

export const formOptionsBusinessCreate = [
  'warehouseNumber',
  'warehouseName',
  'supplierNumber',
  'supplierName',
  'businessNumber',
  'businessName',
  'customerNumber',
  'customerName',
  'salesDestinationNumber',
  'salesDestinationName',
  'package',
  'endUser',
  'baseBusinessResponsibleUser',
];

export const formOptionsAggregateBusinessUniqueKeys = [
  'warehouseNumber',
  'warehouseName',
  'supplierNumber',
  'supplierName',
  'businessNumber',
  'businessName',
  'package',
];

export const formOptionsOrder = [
  'orderNumber',
  'createdOrderDate',
  'desiredDeliveryDate',
  'estimatedTimeOfArrival',
  'orderAmount',
  'orderStatus',
] as const;

export const inputTypeKeyMap = {
  warehouseNumber: 'select',
  warehouseName: 'select',
  supplierNumber: 'select',
  supplierName: 'select',
  userUniqueNumber: 'text',
  businessNumber: 'select',
  businessName: 'select',
  package: 'select',
  unit: 'text',
  manufacturer: 'select',
  orderPackage: 'text',
  purchaseUnitPrice: 'text',
  aggregateBusinessResponsibleUser: 'selectUser',
  leadTime: 'text',
  leadTimeUnit: 'selectUnit',
  inTransitDays: 'text',
  MOQ: 'text',
  stdInventoryIndicesMin: 'text',
  stdInventoryIndicesMax: 'text',
  stdInventoryIndicesUnit: 'selectUnit',
  customerNumber: 'select',
  customerName: 'select',
  salesDestinationNumber: 'select',
  salesDestinationName: 'select',
  endUser: 'select',
  salesUnitPrice: 'text',
  baseBusinessResponsibleUser: 'selectUser',
  orderNumber: 'text',
  createdOrderDate: 'date',
  desiredDeliveryDate: 'date',
  estimatedTimeOfArrival: 'date',
  orderAmount: 'text',
  orderStatus: 'unique',
} as const;

export const validateRuleKeyMap = {
  warehouseNumber: [ValidateRule.Required],
  warehouseName: [ValidateRule.Required],
  supplierNumber: [ValidateRule.Required],
  supplierName: [ValidateRule.Required],
  businessNumber: [ValidateRule.Required],
  businessName: [ValidateRule.Required],
  package: [ValidateRule.Required, ValidateRule.NegativeNumber],
  unit: [],
  manufacturer: [],
  aggregateBusinessResponsibleUser: [],
  userUniqueNumber: [],
  leadTime: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  leadTimeUnit: [ValidateRule.Required],
  inTransitDays: [ValidateRule.HalfWidthNumber],
  MOQ: [ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  orderPackage: [ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  purchaseUnitPrice: [ValidateRule.HalfWidthRealNumber],
  stdInventoryIndicesMin: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  stdInventoryIndicesMax: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  stdInventoryIndicesUnit: [ValidateRule.Required],
  customerNumber: [ValidateRule.Required],
  customerName: [ValidateRule.Required],
  salesDestinationNumber: [ValidateRule.Required],
  salesDestinationName: [ValidateRule.Required],
  endUser: [],
  salesUnitPrice: [ValidateRule.HalfWidthRealNumber],
  baseBusinessResponsibleUser: [],
  orderNumber: [ValidateRule.Required],
  createdOrderDate: [],
  desiredDeliveryDate: [ValidateRule.Required],
  estimatedTimeOfArrival: [ValidateRule.Required],
  orderAmount: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
  orderStatus: [ValidateRule.Required],
} as const;

export const keyMappingForSelectInput: { [key: string]: FilterType } = {
  warehouseNumber: 'WAREHOUSE_CODE',
  warehouseName: 'WAREHOUSE',
  supplierNumber: 'SUPPLIER_CODE',
  supplierName: 'SUPPLIER_NAME',
  businessNumber: 'BUSINESS_CODE',
  businessName: 'BUSINESS_NAME',
  package: 'PACKAGE_AMOUNT',
  manufacturer: 'MANUFACTURER',
  aggregateBusinessResponsibleUser: 'USER',
  leadTimeUnit: 'LEAD_TIME_UNIT',
  stdInventoryIndicesUnit: 'STD_INVENTORY_INDICES_UNIT',
  customerNumber: 'CUSTOMER_CODE',
  customerName: 'CUSTOMER_NAME',
  salesDestinationNumber: 'SALES_DESTINATION_CODE',
  salesDestinationName: 'SALES_DESTINATION_NAME',
  endUser: 'END_USER',
  baseBusinessResponsibleUser: 'USER',
} as const;

export const unitOptions = ['月', '週', '日'];

export const unitOptionsMapping = { month: '月', week: '週', day: '日' };

/**
 * 行う処理タイプの型
 */
export type ProcessType = 'newOption' | 'edit' | 'create';
/**
 * 表示するモーダルタイプの型
 */
export type ModalType = 'loading' | 'commonError' | 'duplicateError' | 'noParentError' | 'success';

export const commonModalTextMapper: Record<ProcessType, Partial<Record<ModalType, { title: string; description?: string }>>> = {
  newOption: {
    loading: { title: '追加中です・・・' },
    commonError: {
      title: 'エラーが発生しました',
      description: 'サーバー内部エラーが発生しました。',
    },
    duplicateError: {
      title: '選択肢が既に存在します',
      description: '追加しようとした選択肢がすでに存在しているため、追加できませんでした。',
    },
  },
  edit: {
    loading: { title: '編集中です・・・' },
    commonError: {
      title: 'エラーが発生しました',
      description: 'サーバー内部エラーが発生しました。',
    },
    duplicateError: {
      title: '既存の取引が存在します',
      description: '編集後の取引は既に存在するため、編集ができませんでした。',
    },
    success: { title: '編集完了しました' },
  },
  create: {
    loading: { title: '追加中です・・・' },
    commonError: {
      title: 'エラーが発生しました',
      description: 'サーバー内部エラーが発生しました。',
    },
    duplicateError: {
      title: '既存の取引が存在します',
      description: '追加しようとした取引が既に存在するため、追加ができませんでした。',
    },
    noParentError: {
      title: '集計取引が存在しません',
      description: '指定した値を持つ集計取引が見つかりませんでした。新規に集計を作成し、その集計に紐付けますか？',
    },
    success: { title: '編集完了しました' },
  },
};

export const checkChildBusinessTableContentHeader: TableContentHeader = {
  contentProperties: [
    {
      key: 'businessId',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['businessId'], propertyType: 'string' },
    },
    {
      key: 'warehouse',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['warehouse'], propertyType: 'string' },
    },
    {
      key: 'supplierName',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['supplierName'], propertyType: 'string' },
    },
    {
      key: 'businessNumber',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['businessNumber'], propertyType: 'string' },
    },
    {
      key: 'businessName',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['businessName'], propertyType: 'string' },
    },
    {
      key: 'package',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['package'], propertyType: 'number' },
    },
    {
      key: 'customerName',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['customerName'], propertyType: 'string' },
    },
    {
      key: 'salesDestinationName',
      isVariableWidth: false,
      isNarrowable: false,
      isEditable: false,
      sortOption: {
        isSortable: false,
        isAsc: true,
      },
      propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
    },
  ],
  options: {
    stableColumnCount: 0,
    resizableHeaderColumn: false,
  },
};

/**
 * 集計編集の区切りカラム
 */
export const AGGREGATE_BUSINESS_DIVIDE_COLUMN: (typeof formOptionsAggregateBusiness)[number] = 'leadTime';
